import React, { Dispatch, FC, SetStateAction } from 'react';
import { DrawerBody, DrawerFooter } from '@fiverr-private/overlay';
import { Button } from '@fiverr-private/button';
import { I18n } from '@fiverr-private/i18n-react';
import { Stack } from '@fiverr-private/layout_components';
import { logger } from '@fiverr-private/obs';
import { StatusToast } from '@fiverr-private/orca';
import { WorkExperience } from '../../../../gql-operations/gql-generated';
import { useSellerDataUpdater } from '../../../../hooks/useSellerDataUpdater';
import useUpsertWorkExperiences, {
    createWorkExperienceUpsertRequestData,
} from '../../../../hooks/useUpsertWorkExperiences';
import { useIsSelfViewFreelancer } from '../../../../hooks/useIsSelfViewFreelancer';
import { Providers, useMonitoring } from '../../../../utils/monitoring';
import { CurrentViewState, TemporaryWorkExperience } from '../../shared/types';
import { T_KEY } from '../../utils';
import workExperienceBIEvents from '../../utils/biEvents';
import WorkExperienceList from '../WorkExperienceList';
import AddExperienceButton from './AddExperienceButton';

interface WorkExperienceListContainerProps {
    setCurrentViewState: Dispatch<SetStateAction<CurrentViewState>>;
    currentViewState: CurrentViewState;
    currentWorkExperiences?: TemporaryWorkExperience[] | null | undefined;
    setIsDrawerOpen: (isDrawerOpen: boolean) => void;
    initialWorkExperiences?: WorkExperience[] | null | undefined;
    setInitialWorkExperiences: Dispatch<SetStateAction<WorkExperience[] | null | undefined>>;
    hasWorkExperience?: boolean | null;
}

const WorkExperienceListContainer: FC<WorkExperienceListContainerProps> = ({
    setCurrentViewState,
    currentViewState,
    currentWorkExperiences,
    setIsDrawerOpen,
    initialWorkExperiences,
    setInitialWorkExperiences,
    hasWorkExperience,
}) => {
    const isSelfView = useIsSelfViewFreelancer();
    const setSellerData = useSellerDataUpdater();
    const { fireEvents } = useMonitoring();

    const setWorkExperienceSellerData = (workExperiences: WorkExperience[] | null | undefined) =>
        setSellerData((seller) => {
            seller.workExperiences.nodes = workExperiences;
        });

    const onSave = () => {
        fireEvents({
            [Providers.BigQuery]: workExperienceBIEvents.clickedOnPublishWorkExperience(),
        });

        if (currentViewState.isDirty) {
            upsertWorkExperiences(createWorkExperienceUpsertRequestData(currentWorkExperiences));
        } else {
            setIsDrawerOpen(false);
        }
    };

    const onSaveResponse = (data?: string) => {
        if (data === undefined) {
            onSaveError();
            return;
        }

        setCurrentViewState?.((state) => ({
            ...state,
            isDirty: false,
        }));

        setIsDrawerOpen(false);
        setInitialWorkExperiences(currentWorkExperiences);
        setWorkExperienceSellerData(currentWorkExperiences);
    };

    const onSaveError = () => {
        const err = new Error(`Failed to upsert work experiences data`);
        logger.error(err);
        StatusToast.raise({
            message: <I18n k={`seller_pages_toolbox.toast.error`} />,
            theme: (StatusToast as any).THEMES.ERROR,
        });
    };

    const { mutate: upsertWorkExperiences, isLoading } = useUpsertWorkExperiences({
        onSuccess: onSaveResponse,
        onError: onSaveError,
    });

    return (
        <>
            <DrawerBody>
                <Stack direction="column" gap="8" alignItems="flexStart">
                    {isSelfView && <AddExperienceButton setCurrentViewState={setCurrentViewState} />}
                    <WorkExperienceList
                        isCard
                        showAll
                        setCurrentViewState={setCurrentViewState}
                        workExperiences={currentWorkExperiences}
                    />
                </Stack>
            </DrawerBody>
            {isSelfView && (
                <DrawerFooter>
                    <Stack justifyContent="flexEnd">
                        <Button
                            type="submit"
                            disabled={
                                (!initialWorkExperiences?.length && !currentWorkExperiences?.length) ||
                                !currentViewState.isDirty
                            }
                            loading={isLoading}
                            onClick={onSave}
                        >
                            <I18n k={`${T_KEY}.${hasWorkExperience ? 'save' : 'publish'}`} />
                        </Button>
                    </Stack>
                </DrawerFooter>
            )}
        </>
    );
};

export default WorkExperienceListContainer;
